if (!customElements.get('pickup-availability')) {
  customElements.define(
    'pickup-availability',
    class PickupAvailability extends HTMLElement {
      constructor() {
        super();

        const errorTemplate = this.querySelector('template');
        this.errorHtml = errorTemplate?.content.firstElementChild?.cloneNode(true) || null;
        this.drawer = null;
        this.onClickRefreshList = this.onClickRefreshList.bind(this);

        if (this.hasAttribute('available')) this.fetchAvailability(this.dataset.variantId);
      }

      fetchAvailability(variantId) {
        if (!variantId) return;

        this.dataset.variantId = variantId;
        this.clearSelectedStore();
        this.setLoading(true);

        let rootUrl = this.dataset.rootUrl || '/';
        if (!rootUrl.endsWith('/')) rootUrl += '/';

        fetch(`${rootUrl}variants/${variantId}/?section_id=pickup-availability`)
          .then((response) => {
            if (!response.ok) throw new Error(`Pickup availability request failed: ${response.status}`);
            return response.text();
          })
          .then((text) => {
            const sectionInnerHTML = new DOMParser()
              .parseFromString(text, 'text/html')
              .querySelector('.shopify-section');

            if (!sectionInnerHTML) throw new Error('Pickup availability section was not returned');
            this.renderPreview(sectionInnerHTML);
          })
          .catch(() => this.renderError());
      }

      onClickRefreshList() {
        this.fetchAvailability(this.dataset.variantId);
      }

      setLoading(isLoading) {
        this.toggleAttribute('aria-busy', isLoading);
        this.querySelector('pickup-availability-preview')?.classList.toggle('ecco-click-collect--loading', isLoading);
      }

      renderError() {
        this.removeDrawer();
        this.innerHTML = '';

        if (!this.errorHtml) {
          this.renderUnavailable('No se pudo cargar la disponibilidad');
          return;
        }

        const errorContent = this.errorHtml.cloneNode(true);
        errorContent.querySelector('[data-pickup-title]').textContent = this.blockTitle;
        this.appendChild(errorContent);
        this.removeAttribute('aria-busy');
        this.querySelector('[data-pickup-refresh]')?.addEventListener('click', this.onClickRefreshList);
      }

      renderUnavailable(message = this.unavailableText) {
        this.removeDrawer();
        this.innerHTML = `
          <pickup-availability-preview class="ecco-click-collect">
            <span class="ecco-click-collect__left" data-pickup-title></span>
            <div class="ecco-click-collect__status">
              <span class="ecco-click-collect__dot ecco-click-collect__dot--unavailable" aria-hidden="true"></span>
              <span class="ecco-click-collect__status-text" data-pickup-status></span>
            </div>
          </pickup-availability-preview>
        `;
        this.querySelector('[data-pickup-title]').textContent = this.blockTitle;
        this.querySelector('[data-pickup-status]').textContent = message;
        this.removeAttribute('available');
        this.removeAttribute('aria-busy');
      }

      renderPreview(sectionInnerHTML) {
        const preview = sectionInnerHTML.querySelector('pickup-availability-preview');
        const drawer = sectionInnerHTML.querySelector('pickup-availability-drawer');

        this.removeDrawer();

        if (!preview) {
          this.renderUnavailable();
          return;
        }

        this.innerHTML = preview.outerHTML;
        const renderedPreview = this.querySelector('pickup-availability-preview');
        const availableCount = Number(renderedPreview.dataset.availableCount || 0);

        renderedPreview.querySelector('[data-pickup-title]').textContent = this.blockTitle;
        let availabilityText = this.availableTemplate.replace('[count]', availableCount);
        if (availableCount === 1) availabilityText = availabilityText.replace(/sucursales/i, 'sucursal');
        renderedPreview.querySelector('[data-pickup-status]').textContent =
          availableCount > 0 ? availabilityText : this.unavailableText;

        const openButton = renderedPreview.querySelector('[data-pickup-open]');
        if (openButton) openButton.textContent = this.storeLinkLabel;

        if (drawer && availableCount > 0) {
          drawer.dataset.ownerId = this.id;
          drawer.querySelector('[data-pickup-modal-title]').textContent = this.modalTitle;
          drawer.querySelectorAll('[data-pickup-select-store]').forEach((button) => {
            button.textContent = this.storeLinkLabel;
          });
          document.body.appendChild(drawer);
          this.drawer = drawer;

          openButton?.addEventListener('click', (event) => drawer.show(event.currentTarget));
          this.setAttribute('available', '');
        } else {
          this.removeAttribute('available');
        }

        this.removeAttribute('aria-busy');
      }

      selectStore(location) {
        const form = document.getElementById(this.dataset.productFormId);
        if (!form || !location?.name) return;

        this.clearSelectedStore();

        const storeNameInput = document.createElement('input');
        storeNameInput.type = 'hidden';
        storeNameInput.name = 'properties[Sucursal de recogida]';
        storeNameInput.value = location.name;
        storeNameInput.dataset.pickupStoreProperty = '';
        form.appendChild(storeNameInput);

        const storeIdInput = document.createElement('input');
        storeIdInput.type = 'hidden';
        storeIdInput.name = 'properties[_ID de sucursal Shopify]';
        storeIdInput.value = location.id || '';
        storeIdInput.dataset.pickupStoreProperty = '';
        form.appendChild(storeIdInput);

        this.dataset.selectedStoreId = location.id || '';
        const status = this.querySelector('[data-pickup-status]');
        if (status) status.textContent = this.selectedTemplate.replace('[store]', location.name);

        const openButton = this.querySelector('[data-pickup-open]');
        if (openButton) openButton.textContent = 'Cambiar sucursal';

        this.dispatchEvent(
          new CustomEvent('pickup-store-selected', {
            bubbles: true,
            detail: { ...location, variantId: this.dataset.variantId },
          })
        );
      }

      clearSelectedStore() {
        const form = document.getElementById(this.dataset.productFormId);
        form?.querySelectorAll('[data-pickup-store-property]').forEach((input) => input.remove());
        delete this.dataset.selectedStoreId;
      }

      removeDrawer() {
        if (!this.drawer) return;
        if (this.drawer.hasAttribute('open')) this.drawer.hide();
        this.drawer.remove();
        this.drawer = null;
      }

      get blockTitle() {
        return this.dataset.blockTitle || 'Recoger en tienda';
      }

      get availableTemplate() {
        return this.dataset.availableTemplate || 'Disponible en [count] sucursales';
      }

      get unavailableText() {
        return this.dataset.unavailableText || 'No disponible para esta talla';
      }

      get storeLinkLabel() {
        return this.dataset.storeLinkLabel || 'Haz clic para ver tiendas disponibles';
      }

      get modalTitle() {
        return this.dataset.modalTitle || 'Selecciona una sucursal';
      }

      get selectedTemplate() {
        return this.dataset.selectedTemplate || 'Recoger en [store]';
      }
    }
  );
}

if (!customElements.get('pickup-availability-drawer')) {
  customElements.define(
    'pickup-availability-drawer',
    class PickupAvailabilityDrawer extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.closeButton = this.querySelector('[data-pickup-close]');
        this.searchInput = this.querySelector('[data-pickup-search]');
        this.locationList = this.querySelector('[data-pickup-location-list]');
        this.locations = Array.from(this.querySelectorAll('[data-pickup-location]'));
        this.noResults = this.querySelector('[data-pickup-no-results]');
        this.mapElement = this.querySelector('[data-pickup-map]');
        this.mapEmpty = this.querySelector('[data-pickup-map-empty]');
        this.markers = new Map();

        this.locations.forEach((location) => {
          const locationName = this.formatLocationName(location.dataset.locationName || '');
          location.dataset.locationName = locationName;
          location.dataset.locationSearch = `${location.dataset.locationSearch || ''} ${locationName}`.trim();
          const nameElement = location.querySelector('.ecco-pickup-location__name');
          if (nameElement) nameElement.textContent = locationName;
        });

        this.closeButton?.addEventListener('click', () => this.hide());
        this.searchInput?.addEventListener('input', () => this.filterLocations());
        this.querySelector('[data-pickup-geolocate]')?.addEventListener('click', () => this.useCurrentLocation());
        this.querySelectorAll('[data-pickup-select-store]').forEach((button) => {
          button.addEventListener('click', () => this.selectStore(button.closest('[data-pickup-location]')));
        });
        this.locations.forEach((location) => {
          location.addEventListener('click', (event) => {
            if (event.target.closest('a, button')) return;
            this.focusLocation(location);
          });
        });
        this.addEventListener('keyup', (event) => {
          if (event.code.toUpperCase() === 'ESCAPE') this.hide();
        });
      }

      show(focusElement) {
        this.focusElement = focusElement;
        this.setAttribute('open', '');
        document.body.classList.add('overflow-hidden', 'ecco-pickup-modal-open');
        trapFocus(this, this.closeButton || this);

        requestAnimationFrame(() => {
          this.initializeMap();
          this.map?.invalidateSize();
        });
      }

      hide() {
        this.removeAttribute('open');
        document.body.classList.remove('overflow-hidden', 'ecco-pickup-modal-open');
        removeTrapFocus(this.focusElement);
      }

      selectStore(locationElement) {
        if (!locationElement || locationElement.dataset.available !== 'true') return;

        const owner = document.getElementById(this.dataset.ownerId);
        owner?.selectStore({
          id: locationElement.dataset.locationId,
          name: locationElement.dataset.locationName,
          address: locationElement.dataset.locationAddress,
          latitude: Number(locationElement.dataset.latitude) || null,
          longitude: Number(locationElement.dataset.longitude) || null,
        });
        this.hide();
      }

      initializeMap() {
        if (this.map || !this.mapElement) return;

        const mappableLocations = this.availableLocations.filter((location) => this.hasCoordinates(location));
        if (!window.L || mappableLocations.length === 0) {
          this.mapElement.hidden = true;
          this.mapEmpty.hidden = false;
          return;
        }

        this.map = window.L.map(this.mapElement, {
          zoomControl: true,
          scrollWheelZoom: true,
        });

        window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
          maxZoom: 19,
        }).addTo(this.map);

        mappableLocations.forEach((location) => {
          const coordinates = this.coordinatesFor(location);
          const marker = window.L.marker(coordinates, {
            icon: window.L.divIcon({
              className: 'ecco-pickup-map-marker',
              html: '<span aria-hidden="true"></span>',
              iconAnchor: [15, 36],
              iconSize: [30, 36],
            }),
          });
          const popupLabel = document.createElement('strong');
          popupLabel.textContent = location.dataset.locationName;
          marker.bindPopup(popupLabel);
          marker.on('click', () => this.focusLocation(location, false));
          marker.addTo(this.map);
          this.markers.set(location.dataset.locationId, marker);
        });

        this.fitMapToLocations(mappableLocations);
      }

      filterLocations() {
        const query = this.normalize(this.searchInput?.value || '');
        const visibleLocations = [];

        this.locations.forEach((location) => {
          const matches = !query || this.normalize(location.dataset.locationSearch || '').includes(query);
          location.hidden = !matches;
          if (matches && location.dataset.available === 'true') visibleLocations.push(location);

          const marker = this.markers.get(location.dataset.locationId);
          if (!marker || !this.map) return;
          if (matches && !this.map.hasLayer(marker)) marker.addTo(this.map);
          if (!matches && this.map.hasLayer(marker)) marker.removeFrom(this.map);
        });

        if (this.noResults) this.noResults.hidden = this.locations.some((location) => !location.hidden);
        this.fitMapToLocations(visibleLocations.filter((location) => this.hasCoordinates(location)));
      }

      focusLocation(location, scrollToCard = true) {
        this.locations.forEach((item) => item.classList.toggle('is-active', item === location));
        if (scrollToCard) location.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

        const marker = this.markers.get(location.dataset.locationId);
        if (marker && this.map) {
          this.map.setView(marker.getLatLng(), Math.max(this.map.getZoom(), 13), { animate: true });
          marker.openPopup();
        }
      }

      useCurrentLocation() {
        const status = this.querySelector('[data-pickup-geolocation-status]');
        if (!navigator.geolocation) {
          this.showGeolocationStatus(status, 'Tu navegador no permite usar la ubicación.');
          return;
        }

        this.showGeolocationStatus(status, 'Buscando tu ubicación…');
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const customerCoordinates = [position.coords.latitude, position.coords.longitude];
            this.locations.forEach((location) => {
              if (!this.hasCoordinates(location)) return;
              const distance = this.distanceInKilometers(customerCoordinates, this.coordinatesFor(location));
              location.dataset.distance = distance;
              const distanceElement = location.querySelector('[data-pickup-distance]');
              if (distanceElement) {
                distanceElement.textContent = `${distance < 10 ? distance.toFixed(1) : Math.round(distance)} km`;
                distanceElement.hidden = false;
              }
            });

            this.locations
              .sort((a, b) => Number(a.dataset.distance || Infinity) - Number(b.dataset.distance || Infinity))
              .forEach((location) => this.locationList.appendChild(location));

            if (this.map && window.L) {
              if (this.customerMarker) this.customerMarker.removeFrom(this.map);
              this.customerMarker = window.L.circleMarker(customerCoordinates, {
                color: '#111',
                fillColor: '#fff',
                fillOpacity: 1,
                radius: 7,
                weight: 3,
              }).addTo(this.map);
              this.map.setView(customerCoordinates, 11, { animate: true });
            }

            this.showGeolocationStatus(status, 'Sucursales ordenadas por cercanía.');
          },
          () => this.showGeolocationStatus(status, 'No pudimos obtener tu ubicación. Revisa los permisos del navegador.'),
          { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
        );
      }

      showGeolocationStatus(element, message) {
        if (!element) return;
        element.textContent = message;
        element.hidden = false;
      }

      fitMapToLocations(locations) {
        if (!this.map || locations.length === 0) return;
        const bounds = locations.map((location) => this.coordinatesFor(location));
        if (bounds.length === 1) this.map.setView(bounds[0], 13);
        else this.map.fitBounds(bounds, { padding: [50, 50], maxZoom: 13 });
      }

      hasCoordinates(location) {
        return (
          location.dataset.latitude !== '' &&
          location.dataset.longitude !== '' &&
          Number.isFinite(Number(location.dataset.latitude)) &&
          Number.isFinite(Number(location.dataset.longitude))
        );
      }

      coordinatesFor(location) {
        return [Number(location.dataset.latitude), Number(location.dataset.longitude)];
      }

      get availableLocations() {
        return this.locations.filter((location) => location.dataset.available === 'true');
      }

      normalize(value) {
        return value
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase()
          .trim();
      }

      formatLocationName(value) {
        const locationName = value.trim();
        return this.normalize(locationName) === 'santa fe ecco' ? 'ECCO SANTA FE' : locationName;
      }

      distanceInKilometers(origin, destination) {
        const toRadians = (degrees) => (degrees * Math.PI) / 180;
        const earthRadius = 6371;
        const latitudeDelta = toRadians(destination[0] - origin[0]);
        const longitudeDelta = toRadians(destination[1] - origin[1]);
        const latitude1 = toRadians(origin[0]);
        const latitude2 = toRadians(destination[0]);
        const haversine =
          Math.sin(latitudeDelta / 2) ** 2 +
          Math.cos(latitude1) * Math.cos(latitude2) * Math.sin(longitudeDelta / 2) ** 2;
        return earthRadius * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
      }
    }
  );
}
